import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
    useAppSidePanelContextProvider
} from "../../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { useState } from "react";
import { PortfolioAddOrUpdateModel } from "../../types/PortfolioAddOrUpdateModel";
import { PortfolioManagerDialog } from "../../components/PortfolioManagerDialog";
import {
    ActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import {
    useActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { PortfolioSelector } from "../../components/PortfolioSelector";
import { PortfolioModel } from "../../types/PortfolioModel";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export function PortfolioSelectorView() {
    const {
        selectedPortfolio,
        createPortfolio,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        portfolios,
        setSelectedPortfolioByName,
        cloneSelectedPortfolio,
    } = useAppSidePanelContextProvider();

    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();
    
    const [isPortfolioManagerDialogOpen, setIsPortfolioManagerDialogOpen] = useState<boolean>(false);
    const [isCreation, setIsCreation] = useState<boolean>(false);
    const [submitAction, setSubmitAction] = useState<(portfolioModel: PortfolioAddOrUpdateModel) => void>();
    const [editingPortfolioModel, setEditingPortfolioModel] = useState<PortfolioAddOrUpdateModel | null>(null);

    const handleCreate = () => {
        setIsCreation(true);
        setSubmitAction(() => async (portfolioAddModel: PortfolioAddOrUpdateModel) => {
            await createPortfolio(portfolioAddModel);
        });
        setIsPortfolioManagerDialogOpen(true);
    };

    const handleUpdate = () => {
        if (!selectedPortfolio) {
            return;
        }
        
        setIsCreation(false);
        setEditingPortfolioModel({
           name: selectedPortfolio.name,
           description: selectedPortfolio.description,
           marginBuffer: selectedPortfolio.marginBuffer,
           inceptionDate: selectedPortfolio.inceptionDate,
           useValuationDate: selectedPortfolio.useValuationDate,
           valuationDate: selectedPortfolio.valuationDate,
        });
        setSubmitAction(() => (portfolioUpdateModel: PortfolioAddOrUpdateModel) => {
            editSelectedPortfolio(portfolioUpdateModel);
        });
        setIsPortfolioManagerDialogOpen(true);
    };

    const handleSave = () => {
        setConfirmedAction(() => saveSelectedPortfolio);
        setConfirmationMessage("Are you sure you want to save the portfolio?");
        setSubmitButtonName("Save");
        setIsActionConfirmationDialogOpen(true);
    }

    const handleClone = () => {
        setConfirmedAction(() => cloneSelectedPortfolio);
        setConfirmationMessage("Are you sure you want to clone the portfolio?");
        setSubmitButtonName("Clone");
        setIsActionConfirmationDialogOpen(true);
    }
    
    const primaryColour = "#FACC2D";
    const secondaryColour = "#FACC2D";
    const secondaryHoverColour = "#FFFFFF";
    const disabledColour = "#c5c5c5";
    
    const actionIconSize = 36;
    
    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                submitButtonName={submitButtonName}
                confirmedParentAction={confirmedAction}
            />
            <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
            >
                <PortfolioManagerDialog
                    isOpen={isPortfolioManagerDialogOpen}
                    setIsOpen={setIsPortfolioManagerDialogOpen}
                    isCreation={isCreation}
                    submitAction={submitAction}
                    editingPortfolioModel={editingPortfolioModel}
                    icon={isCreation ? <AddBoxIcon /> : <EditIcon />}
                />
                <Tooltip title={selectedPortfolio?.description}>
                    <PortfolioSelector
                        portfolios={portfolios}
                        selectedPortfolio={selectedPortfolio}
                        setSelectedPortfolio={setSelectedPortfolioByName}
                        labelText="Portfolios"
                        primaryColour={primaryColour}
                        secondaryColour={secondaryColour}
                        secondaryHoverColour={secondaryHoverColour}
                        disabled={!isAnyPortfolioAvailable(portfolios)}
                    />
                </Tooltip>
                <Box sx={{ pl: 2 }}>
                    <Tooltip
                        title="Create a new portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{ 
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handleCreate}
                        >
                            <AddBoxIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Edit the portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handleUpdate}
                        >
                            <EditIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Clone the portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handleClone}
                        >
                            <ContentCopyIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                                
                {/*{selectedPortfolio ? (*/}
                {/*    <TextField*/}
                {/*        id="standard-multiline-static"*/}
                {/*        label="Description"*/}
                {/*        multiline*/}
                {/*        // disabled*/}
                {/*        rows={1}*/}
                {/*        value={selectedPortfolio.description}*/}
                {/*        variant="standard"*/}
                {/*        size="small"*/}
                {/*        aria-readonly*/}
                {/*        sx={{*/}
                {/*            marginLeft: "10px",*/}
                {/*            width: "500px",*/}
                {/*            // color: secondaryColour,*/}
                {/*            // "&.Mui-focused": {*/}
                {/*            //     color: secondaryHoverColour*/}
                {/*            // }*/}
                
                {/*            // color: primaryColour,*/}
                {/*            // '&:before': {*/}
                {/*            //     borderColor: secondaryColour,*/}
                {/*            // },*/}
                {/*            // '&:after': {*/}
                {/*            //     borderColor: secondaryHoverColour,*/}
                {/*            // },*/}
                {/*            // '&:not(.Mui-disabled):hover::before': {*/}
                {/*            //     borderColor: secondaryColour,*/}
                {/*            // },*/}
                {/*            // '.MuiSvgIcon-root': {*/}
                {/*            //     fill: `${secondaryColour} !important`,*/}
                {/*            // }*/}
                {/*            */}
                {/*            // Root class for the input field*/}
                {/*            '&.MuiStandardInput-root': {*/}
                {/*                color: primaryColour,*/}
                {/*                fontFamily: "Arial",*/}
                {/*                fontWeight: "bold",*/}
                {/*            // Class for the border around the input field*/}
                {/*            '&.MuiStandardInput-notchedStandard': {*/}
                {/*                    borderColor: primaryColour,*/}
                {/*                    borderWidth: "2px",*/}
                {/*                },*/}
                {/*            },*/}
                {/*            // Class for the label of the input field*/}
                {/*            '&.MuiInputLabel-outlined': {*/}
                {/*                color: primaryColour,*/}
                {/*                fontWeight: "bold",*/}
                {/*            },*/}
                {/*        }}*/}
                {/*    />*/}
                {/*) : (*/}
                {/*    <></>*/}
                {/*)}*/}
            </Stack>
        </>
    );
}

function isAnyPortfolioAvailable(portfolios: PortfolioModel[]): boolean {
    return portfolios
        ? portfolios.length != 0
        : false;
}
